import React from 'react';
import { cn } from '@/lib/utils';

interface DCLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Animated DC logo loader for page loading states
 * Premium gold gradient with pulse, arrow draw and tick animations
 */
export function DCLoader({ size = 'lg', className }: DCLoaderProps) {
  const sizeMap = {
    sm: 80,
    md: 120,
    lg: 160,
    xl: 200,
  };

  const dimension = sizeMap[size];

  return (
    <svg
      width={dimension}
      height={dimension}
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Drift Control loading"
      className={cn('dc-loader', className)}
    >
      <defs>
        {/* Gold gradient */}
        <linearGradient id="dcGold" x1="20" y1="20" x2="140" y2="140" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#F5C542"/>
          <stop offset="0.55" stopColor="#D4AF37"/>
          <stop offset="1" stopColor="#B88A2A"/>
        </linearGradient>

        {/* Subtle highlight gradient for ring */}
        <linearGradient id="dcRing" x1="0" y1="0" x2="160" y2="160" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFE8A3" stopOpacity="0.9"/>
          <stop offset="0.6" stopColor="#D4AF37" stopOpacity="0.55"/>
          <stop offset="1" stopColor="#8A5E17" stopOpacity="0.35"/>
        </linearGradient>

        {/* Glow */}
        <filter id="dcGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3.5" result="blur"/>
          <feColorMatrix
            in="blur"
            type="matrix"
            values="
              1 0 0 0 0.96
              0 1 0 0 0.78
              0 0 1 0 0.26
              0 0 0 1 0"
            result="goldBlur"
          />
          <feMerge>
            <feMergeNode in="goldBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      <style>{`
        .dc-pulse {
          transform-origin: 80px 80px;
          animation: dcPulse 1.2s ease-in-out infinite;
        }
        @keyframes dcPulse {
          0%   { transform: scale(0.96); opacity: 0.90; }
          50%  { transform: scale(1.00); opacity: 1.00; }
          100% { transform: scale(0.96); opacity: 0.90; }
        }

        .dc-arrow {
          stroke-dasharray: 220;
          stroke-dashoffset: 220;
          animation: dcDraw 1.2s ease-in-out infinite;
          filter: url(#dcGlow);
        }
        @keyframes dcDraw {
          0%   { stroke-dashoffset: 220; opacity: 0; }
          20%  { opacity: 1; }
          55%  { stroke-dashoffset: 0;  opacity: 1; }
          80%  { opacity: 0.15; }
          100% { stroke-dashoffset: -40; opacity: 0; }
        }

        .dc-ticks {
          transform-origin: 80px 80px;
          animation: dcTicks 1.2s ease-in-out infinite;
          opacity: 0.55;
        }
        @keyframes dcTicks {
          0%   { transform: rotate(0deg);   opacity: 0.35; }
          50%  { transform: rotate(10deg);  opacity: 0.65; }
          100% { transform: rotate(0deg);   opacity: 0.35; }
        }

        .dc-mark {
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif;
          font-weight: 800;
          letter-spacing: -2px;
        }
      `}</style>

      <g className="dc-pulse">
        {/* Outer ring */}
        <circle cx="80" cy="80" r="66" stroke="url(#dcRing)" strokeWidth="10" opacity="0.9" fill="none"/>
        {/* Inner ring */}
        <circle cx="80" cy="80" r="52" stroke="url(#dcRing)" strokeWidth="2" opacity="0.45" fill="none"/>

        {/* Segmented ticks */}
        <g className="dc-ticks">
          <g stroke="url(#dcGold)" strokeWidth="3" strokeLinecap="round">
            {Array.from({ length: 24 }).map((_, i) => (
              <line
                key={i}
                x1="80"
                y1="10"
                x2="80"
                y2="18"
                transform={`rotate(${i * 15} 80 80)`}
              />
            ))}
          </g>
        </g>

        {/* DC mark */}
        <text x="80" y="94" textAnchor="middle" fontSize="56" className="dc-mark" fill="url(#dcGold)">
          DC
        </text>

        {/* Arrow path (draw animation) */}
        <path
          className="dc-arrow"
          d="M34 90 C48 58, 72 46, 98 52 C114 56, 124 66, 128 76"
          stroke="url(#dcGold)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Arrow head */}
        <path
          className="dc-arrow"
          d="M120 64 L134 74 L118 82"
          stroke="url(#dcGold)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
}

interface DCButtonLoaderProps {
  className?: string;
  size?: number;
}

/**
 * Compact DC loader for button loading states
 * Uses forwardRef to properly handle refs from Suspense and other components
 */
export const DCButtonLoader = React.forwardRef<SVGSVGElement, DCButtonLoaderProps>(
  ({ className, size = 20 }, ref) => {
    const viewBoxSize = 40;
    
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Carregando"
        className={cn('inline-block align-middle', className)}
      >
      <defs>
        <linearGradient id="dcGoldBtn" x1="6" y1="6" x2="34" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#F5C542"/>
          <stop offset="0.55" stopColor="#D4AF37"/>
          <stop offset="1" stopColor="#B88A2A"/>
        </linearGradient>

        <filter id="dcGlowBtn" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.6" result="blur"/>
          <feColorMatrix
            in="blur"
            type="matrix"
            values="
              1 0 0 0 0.96
              0 1 0 0 0.78
              0 0 1 0 0.26
              0 0 0 1 0"
            result="goldBlur"
          />
          <feMerge>
            <feMergeNode in="goldBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      <style>{`
        .dc-ring {
          transform-origin: 20px 20px;
          animation: ringRot 0.9s linear infinite;
          opacity: 0.35;
        }
        @keyframes ringRot {
          to { transform: rotate(360deg); }
        }

        .dc-btn-arrow {
          stroke-dasharray: 90;
          stroke-dashoffset: 90;
          animation: arrowDraw 0.9s ease-in-out infinite;
          filter: url(#dcGlowBtn);
        }
        @keyframes arrowDraw {
          0%   { stroke-dashoffset: 90; opacity: 0; }
          15%  { opacity: 1; }
          55%  { stroke-dashoffset: 0;  opacity: 1; }
          80%  { opacity: 0.2; }
          100% { stroke-dashoffset: -18; opacity: 0; }
        }
      `}</style>

      {/* subtle ring */}
      <circle
        className="dc-ring"
        cx="20"
        cy="20"
        r="16"
        stroke="url(#dcGoldBtn)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="3 5"
        fill="none"
      />

      {/* DC mini */}
      <text
        x="20"
        y="24"
        textAnchor="middle"
        fontSize="12"
        fontWeight="800"
        fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial"
        fill="url(#dcGoldBtn)"
        opacity="0.95"
      >
        DC
      </text>

      {/* arrow mini */}
      <path
        className="dc-btn-arrow"
        d="M10 23 C14 14, 22 12, 28 15 C31 16.5, 33 19, 34 21"
        stroke="url(#dcGoldBtn)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        className="dc-btn-arrow"
        d="M30.5 16.5 L34.8 20.2 L29.8 22.8"
        stroke="url(#dcGoldBtn)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
});

DCButtonLoader.displayName = 'DCButtonLoader';

interface DCContentLoaderProps {
  className?: string;
  message?: string;
}

/**
 * DC loader for content/block loading states
 * Uses the compact DC mini loader with optional message
 */
export function DCContentLoader({ className, message }: DCContentLoaderProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 py-6', className)}>
      <DCButtonLoader size={32} />
      {message && (
        <span className="text-xs text-muted-foreground">{message}</span>
      )}
    </div>
  );
}

interface DCCardLoaderProps {
  className?: string;
}

/**
 * DC loader for card/section loading states
 * Centered with subtle background
 */
export function DCCardLoader({ className }: DCCardLoaderProps) {
  return (
    <div className={cn(
      'flex items-center justify-center min-h-[120px] rounded-lg bg-muted/20',
      className
    )}>
      <DCButtonLoader size={28} />
    </div>
  );
}
